import { clsx } from 'clsx';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import type { LinkBlockStateType, LinkComponentType } from '@unifyapps/defs/types/blocks';
import { Link } from '@unifyapps/ui/components/Link';
import { memo, type SyntheticEvent, useCallback } from 'react';
import { getConditionalIcon } from '@unifyapps/carbon/utils/conditionalValues';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import { typographyVariants } from '@unifyapps/ui/components/Typography/styles';
import { resolveUrl } from '@unifyapps/carbon/utils/url/resolveUrl';
import { decoratorVariants } from './styles';

function LinkBlock({
  attributesAndEventListeners,
  events,
  computedBlockState,
  dndRef,
}: BlockComponentProps<LinkComponentType, LinkBlockStateType, LinkBlockStateType>) {
  const { content, appearance } = computedBlockState;

  const { variant = 'text-sm', weight, styles, color = 'brand' } = appearance ?? {};

  const { emitEvent } = useBlockEvents(events);
  const onClick = useCallback(
    (e: SyntheticEvent) => {
      void emitEvent({
        eventType: EventTypeEnum.OnClick,
        domEvent: e,
      });
    },
    [emitEvent],
  );

  const { className } = useBlockAppearanceStyles({
    appearanceStyles: styles,
  });

  const externalLinkProps = content.openInNewTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Link
      className={clsx(className, typographyVariants({ variant, weight }))}
      disabled={appearance?.disabled}
      endDecoratorComponent={getConditionalIcon(appearance?.endDecorator)}
      href={content.url}
      onClick={onClick}
      resolveUrl={resolveUrl}
      startDecoratorComponent={getConditionalIcon(appearance?.startDecorator)}
      underline={appearance?.underline}
      {...externalLinkProps}
      {...attributesAndEventListeners}
      color={color}
      decoratorClassName={decoratorVariants({ variant })}
      ref={dndRef}
    >
      {content.label ?? content.url}
    </Link>
  );
}

export default memo(LinkBlock);
